/**
 * Goal Announce Modal — shown after goal creation.
 * Asks the user if they want to announce the goal to the feed.
 */

import React from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { Megaphone } from 'lucide-react'

interface GoalAnnounceModalProps {
  isOpen: boolean
  isPending: boolean
  onYes: () => void
  onNo: () => void
}

export const GoalAnnounceModal: React.FC<GoalAnnounceModalProps> = ({
  isOpen,
  isPending,
  onYes,
  onNo,
}) => {
  const { t } = useTranslation()

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onNo}
          />

          {/* Card */}
          <motion.div
            initial={{ scale: 0.92, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.92, opacity: 0 }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            className="relative w-full max-w-sm rounded-2xl bg-[var(--color-bg)] border border-white/[0.08] p-6 flex flex-col items-center text-center"
          >
            <div className="w-14 h-14 rounded-2xl bg-primary/[0.1] flex items-center justify-center mb-4">
              <Megaphone size={26} className="text-primary" />
            </div>

            <h3 className="text-base font-display font-bold text-text mb-1">
              {t('goals.announceModal.title')}
            </h3>
            <p className="text-[13px] text-text-hint leading-relaxed mb-6">
              {t('goals.announceModal.desc')}
            </p>

            <div className="flex gap-3 w-full">
              <button
                onClick={onNo}
                disabled={isPending}
                className="flex-1 h-11 rounded-xl bg-white/[0.06] border border-white/[0.08] text-sm text-text-hint font-medium hover:bg-white/[0.1] transition-colors tap-highlight-transparent disabled:opacity-40"
              >
                {t('goals.announceModal.no')}
              </button>
              <button
                onClick={onYes}
                disabled={isPending}
                className="flex-1 h-11 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors tap-highlight-transparent disabled:opacity-50"
              >
                {isPending ? '...' : t('goals.announceModal.yes')}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}
